import { ComponentType, lazy, LazyExoticComponent } from "react";

type ModuleFactory<T extends ComponentType<any>> = () => Promise<{
  default: T;
}>;

// One session-scoped guard for the whole app: once we have force-reloaded to
// recover from a stale chunk, we do NOT reload again (avoids an infinite loop
// when a chunk is genuinely broken rather than merely stale).
const RELOAD_GUARD_KEY = "chunkReloadAttempted";

/**
 * True for the family of errors that mean "the JS/CSS chunk could not be
 * downloaded" — as opposed to a real runtime error thrown while evaluating the
 * module. Only the former is recoverable by reloading.
 */
function isChunkLoadError(error: unknown): boolean {
  const err = error as { name?: string; message?: string } | null;
  if (!err) return false;
  const name = err.name || "";
  const message = err.message || "";
  return (
    name === "ChunkLoadError" ||
    /loading chunk [\d]+ failed/i.test(message) ||
    /loading css chunk [\w]+ failed/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message)
  );
}

/**
 * Drop-in replacement for `React.lazy` that survives a failed chunk download.
 *
 * Why this exists: with route-level code splitting, a client still holding a
 * **stale cached `index.html`** after a new deploy asks for chunk hashes that
 * no longer exist on the server (404) → `ChunkLoadError` → the route renders
 * nothing / crashes the tree. To recover we:
 *   1. retry the dynamic import a couple of times (handles a transient network
 *      blip on an otherwise-valid chunk), then
 *   2. if it is a chunk-load failure, force ONE full-page reload so the browser
 *      fetches the fresh `index.html` and the current chunk hashes.
 *
 * A `sessionStorage` guard makes the reload strictly one-shot: if the chunk
 * still fails after the reload (genuinely broken, not stale), we surface the
 * error to the nearest error boundary instead of looping. Any successful lazy
 * load clears the guard so a later, unrelated stale deploy can recover again.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: ModuleFactory<T>,
  retries = 2,
  intervalMs = 500
): LazyExoticComponent<T> {
  return lazy(
    () =>
      new Promise<{ default: T }>((resolve, reject) => {
        const attempt = (remaining: number): void => {
          factory()
            .then((mod) => {
              window.sessionStorage.removeItem(RELOAD_GUARD_KEY);
              resolve(mod);
            })
            .catch((error: unknown) => {
              if (remaining > 0) {
                setTimeout(() => attempt(remaining - 1), intervalMs);
                return;
              }

              const alreadyReloaded =
                window.sessionStorage.getItem(RELOAD_GUARD_KEY) === "true";

              if (isChunkLoadError(error) && !alreadyReloaded) {
                window.sessionStorage.setItem(RELOAD_GUARD_KEY, "true");
                window.location.reload();
                return;
              }

              reject(error);
            });
        };

        attempt(retries);
      })
  );
}
